// src/App.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import Landing from './Landing.js';
import Login from './Login.js';
import AuthService from './AuthService.js';
import TermsOfService from './TermsOfService.js';
import PrivacyPolicy from './PrivacyPolicy.js';
import APIService from './APIService.js';
import { GameProvider, clearGameCache } from './game/GameContext.js';
import GameLobby from './game/GameLobby.js';
import PatientRoom from './game/PatientRoom.js';
import UserSettings from './game/UserSettings.js';

function ProtectedRoute({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Wraps the entire game section so context persists across route changes
function GameSection({ user, onLogout, initialDataPromise }) {
  return (
    <GameProvider initialDataPromise={initialDataPromise}>
      <Routes>
        <Route path="/" element={<GameLobby user={user} onLogout={onLogout} />} />
        <Route path="/patient/:caseId" element={<PatientRoom />} />
        <Route path="/settings" element={<UserSettings user={user} onLogout={onLogout} />} />
        <Route path="*" element={<Navigate to="/game" replace />} />
      </Routes>
    </GameProvider>
  );
}

function App() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const gameDataPromiseRef = useRef(null);

  const prefetchGameData = useCallback(() => {
    gameDataPromiseRef.current = Promise.all([
      APIService.getTests(),
      APIService.getDiagnoses(),
      APIService.getLevels(),
      APIService.getGameProgress().catch(() => null),
      APIService.getBackgroundQuestions().catch(() => null),
      APIService.getPhysicalExam().catch(() => null),
    ]);
  }, []);

  useEffect(() => {
    APIService.setNavigationCallback(navigate);
  }, [navigate]);

  useEffect(() => {
    const currentUser = AuthService.getUser();
    if (currentUser) {
      prefetchGameData();
      if (!currentUser.email) {
        APIService.getUser()
          .then(response => {
            if (response?.email) {
              const updatedUser = { ...currentUser, email: response.email };
              localStorage.setItem('user', JSON.stringify(updatedUser));
              setUser(updatedUser);
            } else {
              setUser(currentUser);
            }
          })
          .catch(() => setUser(currentUser))
          .finally(() => setLoading(false));
        return;
      }
    }
    setUser(currentUser || null);
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    const checkTokenValidity = async () => {
      if (!AuthService.isAuthenticated()) {
        const refreshed = await AuthService.refreshAccessToken();
        if (!refreshed) await handleLogout();
      }
    };
    const interval = setInterval(checkTokenValidity, 60 * 1000);
    return () => clearInterval(interval);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = (userData) => {
    prefetchGameData();
    setUser(userData);
    navigate('/game');
  };

  const handleLogout = async () => {
    clearGameCache();
    gameDataPromiseRef.current = null;
    await AuthService.logout();
    setUser(null);
    navigate('/');
  };

  if (loading) return null;

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={user ? <Navigate to="/game" replace /> : <Landing />} />
        <Route path="/login" element={user ? <Navigate to="/game" replace /> : <Login onLogin={handleLogin} initialTab="login" />} />
        <Route path="/signup" element={user ? <Navigate to="/game" replace /> : <Login onLogin={handleLogin} initialTab="signup" />} />
        <Route path="/terms" element={<TermsOfService onBack={() => navigate(-1)} />} />
        <Route path="/privacy" element={<PrivacyPolicy onBack={() => navigate(-1)} />} />
        <Route
          path="/game/*"
          element={
            <ProtectedRoute user={user}>
              <GameSection user={user} onLogout={handleLogout} initialDataPromise={gameDataPromiseRef.current} />
            </ProtectedRoute>
          }
        />
        <Route path="/dashboard/*" element={user ? <Navigate to="/game" replace /> : <Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
